import {
  SegmentedButton,
  Option,
  FormRow,
  Form,
  ValidInput,
  Checkbox,
  ButtonRow,
  Button,
} from '@op/opds.opux';
import { IconDelete } from '@op/opds.opux-icons/action';
import { Modal } from '@op/opds.opux-modal';
import {
  validateFinnishSSN,
  validateBusinessId as validateBusinessIdOPDS,
  getErrorMessage,
  validateString,
} from '@op/opds.validate';
import { type ValidationErrors } from 'final-form';
import { type Dispatch, type SetStateAction, useState, useEffect } from 'react';
import styles from './BeneficiarySelect.module.scss';
import { APP_ELEMENT_ID } from '../../../constants';
import { type StandardAuthenticatedState } from '../../types/initialState';
import {
  NamedBeneficiaryType,
  type NamedCompanyOrCommunityBeneficiary,
  type NamedPersonBeneficiary,
} from 'dto/beneficiary';
import i18n, { getCurrentLanguage } from 'i18n';
import { useAppStore } from 'state/store';
import { getInitialState } from 'utils';
import { focusFirstError, required } from 'utils/validators';

export interface NamedBeneficiaryModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  addBeneficiary: (
    beneficiary: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
  ) => void;
  editableBeneficiary:
    | {
        index: number;
        value: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary;
      }
    | undefined;
  setEditable: Dispatch<
    SetStateAction<
      | {
          index: number;
          value: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary;
        }
      | undefined
    >
  >;
  edit: (
    index: number,
    beneficiary: NamedPersonBeneficiary | NamedCompanyOrCommunityBeneficiary
  ) => void;
  delete: () => void;
  handleCloseModal: () => void;
}

export default function NamedBeneficiaryModal(
  props: Readonly<NamedBeneficiaryModalProps>
) {
  const { partyInformation } = getInitialState() as StandardAuthenticatedState;
  const namedBeneficiaries = useAppStore.use.namedBeneficiaries();
  const currentUserSsn = partyInformation.ssn;

  const [namedBeneficiaryType, setNamedBeneficiaryType] = useState<
    NamedBeneficiaryType | string
  >(NamedBeneficiaryType.PERSON);

  // Person form
  const [firstName, setFirstName] = useState<string>();
  const [lastName, setLastName] = useState<string>();
  const [ssn, setSsn] = useState<string>();
  const [percentage, setPercentage] = useState<number>();
  const [maritalRightsExcluded, setMaritalRightsExcluded] =
    useState<boolean>(false);

  // Company form
  const [name, setName] = useState<string>();
  const [businessId, setBusinessId] = useState<string>();
  const [companyPercentage, setCompanyPercentage] = useState<number>();

  // Flag for delete confirm
  const [deleting, setDeleting] = useState<boolean>(false);

  // Input validator errors
  const [errors, setErrors] = useState<ValidationErrors>();

  const compileBeneficiary = (event: any) => {
    event.preventDefault();

    // Validate input
    const _errors = validateForm();
    setErrors(_errors);

    // No errors => continue
    if (!_errors || Object.keys(_errors).length === 0) {
      let beneficiary:
        | NamedPersonBeneficiary
        | NamedCompanyOrCommunityBeneficiary;

      switch (namedBeneficiaryType) {
        case NamedBeneficiaryType.PERSON:
          beneficiary = {
            namedBeneficiaryType,
            percentage,
            ssn,
            firstName,
            lastName,
            maritalRightsExcluded,
          };
          addOrEditBeneficiary(beneficiary);
          break;

        case NamedBeneficiaryType.COMPANY_OR_COMMUNITY:
          beneficiary = {
            namedBeneficiaryType,
            percentage: companyPercentage,
            businessId,
            name,
          };
          addOrEditBeneficiary(beneficiary);
          break;
      }
    } else focusFirstError(_errors);
  };

  const addOrEditBeneficiary = (beneficiary: any): void => {
    if (props.editableBeneficiary)
      props.edit(props.editableBeneficiary.index, beneficiary);
    else props.addBeneficiary(beneficiary);
    if (beneficiary) props.setOpen(false);
  };

  const update = (state: any) => {
    // Populate form on Open...
    if (state.open) {
      // Editing an existing beneficiary..
      if (props.editableBeneficiary?.value) {
        setDeleting(false);
        setNamedBeneficiaryType(
          props.editableBeneficiary.value.namedBeneficiaryType
        );
        // Person
        if ('ssn' in props.editableBeneficiary.value) {
          setFirstName(props.editableBeneficiary.value.firstName);
          setLastName(props.editableBeneficiary.value.lastName);
          setSsn(props.editableBeneficiary.value.ssn);
          setPercentage(props.editableBeneficiary.value.percentage);
          setMaritalRightsExcluded(
            props.editableBeneficiary.value.maritalRightsExcluded ?? false
          );

          setName(undefined);
          setBusinessId(undefined);
          setCompanyPercentage(undefined);

          // Company
        } else if ('businessId' in props.editableBeneficiary.value) {
          setName(props.editableBeneficiary.value.name);
          setBusinessId(props.editableBeneficiary.value.businessId);
          setCompanyPercentage(props.editableBeneficiary.value.percentage);

          setFirstName(undefined);
          setLastName(undefined);
          setSsn(undefined);
          setPercentage(undefined);
          setMaritalRightsExcluded(false);
        }
        // ..or adding a new one
      } else {
        setFirstName(undefined);
        setLastName(undefined);
        setSsn(undefined);
        setPercentage(undefined);
        setMaritalRightsExcluded(false);
        setName(undefined);
        setBusinessId(undefined);
        setCompanyPercentage(undefined);
      }
      // ...and clear it on Close
    } else {
      props.setEditable(undefined);
    }
    props.setOpen(state.open);
  };

  const deleteBeneficiary = (event: any) => {
    event.preventDefault();
    if (deleting) {
      props.delete();
    } else {
      setDeleting(!deleting);
    }
  };

  useEffect(() => {
    if (deleting) {
      const deleteButtonElement = document.getElementById(
        'beneficiary-modal-delete-confirm-button'
      );
      if (deleteButtonElement) {
        deleteButtonElement.focus();
      }
    }
  }, [deleting]);

  const namedPersonBeneficiaries = namedBeneficiaries.filter(
    (b) => b.namedBeneficiaryType === NamedBeneficiaryType.PERSON
  ) as NamedPersonBeneficiary[];

  const namedCompanyOrCommunityBeneficiaries = namedBeneficiaries.filter(
    (b) => b.namedBeneficiaryType === NamedBeneficiaryType.COMPANY_OR_COMMUNITY
  ) as NamedCompanyOrCommunityBeneficiary[];

  const validateForm = (): ValidationErrors => {
    let _errors = {};

    if (namedBeneficiaryType === NamedBeneficiaryType.PERSON) {
      _errors = {
        firstName: validateName(30, firstName),
        lastName: validateName(30, lastName),
        ssn: validateSSN(ssn),
        percentage: validatePercentage(percentage),
      };
    } else if (
      namedBeneficiaryType === NamedBeneficiaryType.COMPANY_OR_COMMUNITY
    ) {
      _errors = {
        name: validateName(255, name),
        businessId: validateBusinessId(businessId),
        companyPercentage: validatePercentage(companyPercentage),
      };
    }
    // eslint-disable-next-line node/no-unsupported-features/es-builtins
    const filtered = Object.fromEntries(
      Object.entries(_errors).filter(
        (entry) => !isNullArray(entry[1]) && entry[1] !== undefined
      )
    );
    return filtered;
  };

  // onChange handlers
  // Person
  const firstNameChanged = (value: string): void => {
    setFirstName(value);
    setErrors({ ...errors, firstName: validateName(30, value) });
  };

  const lastNameChanged = (value: string): void => {
    setLastName(value);
    setErrors({ ...errors, lastName: validateName(30, value) });
  };

  const ssnChanged = (value: string): void => {
    setSsn(value);
    if (value.length > 10)
      setErrors({
        ...errors,
        ssn: validateSSN(value),
      });
  };

  const percentageChanged = (value: number): void => {
    setPercentage(value);
    setErrors({ ...errors, percentage: validatePercentage(value) });
  };

  // Company
  const nameChanged = (value: string): void => {
    setName(value);
    setErrors({ ...errors, name: validateName(255, value) });
  };

  const businessIdChanged = (value: string): void => {
    setBusinessId(value);
    if (value.length > 8)
      setErrors({ ...errors, businessId: validateBusinessId(value) });
  };

  const companyPercentageChanged = (value: number): void => {
    setCompanyPercentage(value);
    setErrors({
      ...errors,
      companyPercentage: validatePercentage(value),
    });
  };

  // Field validators ...
  const validateName = (max: number, value?: string) => {
    const error =
      required(value) ?? getLengthOutOfBoundsErrorMessage(1, max, value);
    const message = getMessage(error);
    return message;
  };

  const validateSSN = (value?: string) => {
    const error = required(value) ?? getSsnErrorMessage(value);
    const message = getMessage(error);
    setErrors({ ...errors, ssn: message });
    return message;
  };

  const validatePercentage = (value?: number) => {
    const error =
      percentageOutOfRange(1, 100, value) ?? percentageNotAnInteger(value);
    const message = getMessage(error);
    return message;
  };

  const validateBusinessId = (value?: string) => {
    const error = required(value) ?? getBusinessIdErrorMessage(value);
    const message = getMessage(error);
    setErrors({ ...errors, businessId: message });
    return message;
  };

  // ... and their utils
  const getSsnErrorMessage = (value?: string): string | undefined => {
    const error = validateFinnishSSN(value ?? '');
    if (error instanceof Error) return 'beneficiarySelect.verifySsn';
    else if (value && namedPersonBeneficiaries && containsDuplicateSSN(value))
      return 'beneficiarySelect.duplicateSsn';
    else if (value === currentUserSsn)
      return 'beneficiarySelect.ssnSameAsCurrentUser';
    return undefined;
  };

  const getBusinessIdErrorMessage = (value?: string): string | undefined => {
    const error = validateBusinessIdOPDS(value ?? '');
    if (error instanceof Error) {
      return 'beneficiarySelect.verifyBusinessId';
    } else if (
      value &&
      namedCompanyOrCommunityBeneficiaries &&
      containsDuplicateBusinessId(value)
    )
      return 'beneficiarySelect.duplicateBusinessId';
    return undefined;
  };

  const isNullArray = (value: any): boolean => {
    if (Array.isArray(value))
      return value.every((error) => error === undefined);
    return false;
  };

  const getLengthOutOfBoundsErrorMessage = (
    min: number,
    max: number,
    value?: string
  ): string | undefined => {
    if (value) {
      const error = validateString(value, min, max);
      if (error instanceof Error)
        return getErrorMessage(getCurrentLanguage(), error);
    }
    return undefined;
  };

  const percentageNotAnInteger = (value?: number) => {
    if (!Number.isInteger(value)) {
      return 'beneficiarySelect.verifyPercentage';
    }
  };

  const percentageOutOfRange = (
    min: number,
    max: number,
    value?: number
  ): string | undefined => {
    if (!value) return 'beneficiarySelect.verifyPercentage';
    else if (value) {
      if (value < min || value > max)
        return 'beneficiarySelect.verifyPercentage';
    }
  };

  const getMessage = (error?: string): string | undefined => {
    return error ? i18n.t(error) : undefined;
  };

  const containsDuplicateSSN = (ssnToCheck: string): boolean => {
    // if the user is adding a new beneficiary the SsnToCheck can not be present in the namedPersonBenefifiaries
    // but if the user is editing a benefiriacy it is needed to check that the only
    // allowed duplicate is ssnToCheck and props.editableBeneficiary.value.ssn
    if (props.editableBeneficiary) {
      if (
        // if the ssnToCheck is equal to the one user started editing, we can return false
        (props.editableBeneficiary.value as NamedPersonBeneficiary).ssn ===
        ssnToCheck
      ) {
        return false;
      } else {
        return namedPersonBeneficiaries.map((b) => b.ssn).includes(ssnToCheck);
      }
    } else {
      return namedPersonBeneficiaries.map((b) => b.ssn).includes(ssnToCheck);
    }
  };

  const containsDuplicateBusinessId = (businessIdToCheck: string): boolean => {
    if (props.editableBeneficiary) {
      if (
        // if the ssnToCheck is equal to the one user started editing, we can return false
        (props.editableBeneficiary.value as NamedCompanyOrCommunityBeneficiary)
          .businessId === businessIdToCheck
      ) {
        return false;
      } else {
        return namedCompanyOrCommunityBeneficiaries
          .map((b) => b.businessId)
          .includes(businessIdToCheck);
      }
    } else {
      return namedCompanyOrCommunityBeneficiaries
        .map((b) => b.businessId)
        .includes(businessIdToCheck);
    }
  };

  const deleteButtonKey = deleting
    ? 'beneficiary-modal-delete-confirm-button'
    : 'beneficiary-modal-delete-button';

  const setFocusToAddBeneficiaryButton = () => {
    const button = document.getElementById('namedBeneficiaries');
    button?.focus();
  };

  return (
    <Modal
      appElement={document.getElementById(APP_ELEMENT_ID) ?? undefined}
      className={styles.namedModal}
      isOpen={props.open}
      onAfterClose={() => {
        setErrors(undefined);
        props.handleCloseModal();
        setFocusToAddBeneficiaryButton();
      }}
      title={
        props.editableBeneficiary
          ? i18n.t('beneficiarySelect.editBeneficiary')
          : i18n.t('beneficiarySelect.addBeneficiary')
      }
      titleTagName='h3'
      onUpdate={update}
      footer={
        <div className={styles.modalButtonRow}>
          <ButtonRow stacked>
            <Button
              finish
              type='submit'
              form='named-beneficiary-form'
              data-testid='submit'>
              {i18n.t('beneficiarySelect.continue')}
            </Button>
            <Button
              onClick={(event) => {
                event.preventDefault();
                props.setOpen(false);
              }}
              data-testid='beneficiary-modal-cancel-button'>
              {i18n.t('beneficiarySelect.abort')}
            </Button>
          </ButtonRow>
        </div>
      }>
      <Form
        id='named-beneficiary-form'
        data-testid='named-beneficiary-form'
        stacked
        {...({ noValidate: true } as any)}
        onSubmit={(event) => {
          compileBeneficiary(event);
        }}>
        <SegmentedButton
          data-testid='named-beneficiary-type-select'
          disabled={!!props.editableBeneficiary}
          className={!!props.editableBeneficiary ? styles.hidden : ''}
          onChange={(ev, val) => {
            setNamedBeneficiaryType(val);
          }}
          segmentMinWidth='50%'
          value={namedBeneficiaryType}>
          <Option value={NamedBeneficiaryType.PERSON}>
            {i18n.t('beneficiarySelect.person')}
          </Option>
          <Option value={NamedBeneficiaryType.COMPANY_OR_COMMUNITY}>
            {i18n.t('beneficiarySelect.companyOrCommunity')}
          </Option>
        </SegmentedButton>
        {namedBeneficiaryType === NamedBeneficiaryType.PERSON && (
          <div id='personForm'>
            <FormRow
              label={i18n.t('beneficiarySelect.firstName')}
              labelFor='firstName'>
              <ValidInput
                autoComplete='off'
                tabIndex={0}
                id='firstName'
                data-testid='firstName'
                value={firstName ?? ''}
                error={errors?.firstName}
                onChange={(ev, val) => {
                  firstNameChanged(val);
                }}
                aria-required={true}
              />
            </FormRow>
            <FormRow
              label={i18n.t('beneficiarySelect.lastName')}
              labelFor='lastName'>
              <ValidInput
                autoComplete='off'
                tabIndex={0}
                id='lastName'
                data-testid='lastName'
                value={lastName ?? ''}
                error={errors?.lastName}
                onChange={(ev, val) => {
                  lastNameChanged(val);
                }}
                aria-required={true}
              />
            </FormRow>
            <FormRow label={i18n.t('beneficiarySelect.ssn')} labelFor='ssn'>
              <ValidInput
                autoComplete='off'
                tabIndex={0}
                id='ssn'
                data-testid='ssn'
                value={ssn ?? ''}
                error={errors?.ssn}
                onBlur={(event: any) => {
                  event.preventDefault();
                  validateSSN(ssn);
                }}
                onChange={(ev, val) => {
                  ssnChanged(val);
                }}
                aria-required={true}
              />
            </FormRow>
            <FormRow
              label={i18n.t('beneficiarySelect.percentage')}
              labelFor='percentage'>
              <ValidInput
                autoComplete='off'
                className={styles.veryNarrow}
                tabIndex={0}
                min={1}
                max={100}
                id='percentage'
                data-testid='percentage'
                amount
                unit={'%'}
                unitPos='outside'
                value={percentage?.toString() ?? ''}
                error={errors?.percentage}
                onChange={(ev, val) => {
                  percentageChanged(+val);
                }}
                aria-required={true}
              />
            </FormRow>
            <FormRow className={styles.shrinkFont}>
              <Checkbox
                checked={maritalRightsExcluded}
                data-testid='marital-rights-exclusion-checkbox'
                onChange={(ev, val) => {
                  setMaritalRightsExcluded(val);
                }}>
                {i18n.t('beneficiarySelect.maritalRightsExclusion')}
              </Checkbox>
            </FormRow>
          </div>
        )}
        {namedBeneficiaryType === NamedBeneficiaryType.COMPANY_OR_COMMUNITY && (
          <div id='companyForm'>
            <FormRow label={i18n.t('beneficiarySelect.name')} labelFor='name'>
              <ValidInput
                autoComplete='off'
                tabIndex={0}
                id='name'
                data-testid='name'
                value={name ?? ''}
                error={errors?.name}
                onChange={(ev, val) => {
                  nameChanged(val);
                }}
                aria-required={true}
              />
            </FormRow>
            <FormRow
              label={i18n.t('beneficiarySelect.businessId')}
              labelFor='businessId'>
              <ValidInput
                autoComplete='off'
                tabIndex={0}
                id='businessId'
                data-testid='businessId'
                value={businessId ?? ''}
                error={errors?.businessId}
                onBlur={(event: any) => {
                  event.preventDefault();
                  validateBusinessId(businessId);
                }}
                onChange={(ev, val) => {
                  businessIdChanged(val);
                }}
                aria-required={true}
              />
            </FormRow>
            <FormRow
              label={i18n.t('beneficiarySelect.percentage')}
              labelFor='companyPercentage'>
              <ValidInput
                autoComplete='off'
                className={styles.veryNarrow}
                tabIndex={0}
                min={1}
                max={100}
                id='companyPercentage'
                data-testid='companyPercentage'
                amount
                unit={'%'}
                unitPos='outside'
                value={companyPercentage?.toString() ?? ''}
                error={errors?.companyPercentage}
                onChange={(ev, val) => {
                  companyPercentageChanged(+val);
                }}
                aria-required={true}
              />
            </FormRow>
          </div>
        )}
        {props.editableBeneficiary && (
          <div className={styles.deleteButton}>
            <Button
              id={deleteButtonKey}
              key={deleteButtonKey}
              type='button'
              icon={IconDelete}
              compact={!deleting}
              primary={deleting}
              onClick={(event) => {
                deleteBeneficiary(event);
              }}
              data-testid={deleteButtonKey}>
              {deleting
                ? i18n.t('beneficiarySelect.confirmDelete')
                : i18n.t('beneficiarySelect.deleteBeneficiary')}
            </Button>
          </div>
        )}
      </Form>
    </Modal>
  );
}
